import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { PaintRegionStatus } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';

/**
 * Default labels for paint region status values.
 * These are used when no custom labels are configured at the structure level.
 */
const DEFAULT_STATUS_LABELS: Record<PaintRegionStatus, string> = {
  [PaintRegionStatus.InProgress]: 'In Progress',
  [PaintRegionStatus.Scheduled]: 'Scheduled',
  [PaintRegionStatus.InspectionPending]: 'Inspection pending',
  [PaintRegionStatus.NoWorkRequired]: 'No work required',
  [PaintRegionStatus.Completed]: 'Completed',
  [PaintRegionStatus.PartiallyCompleted]: 'Partially Completed',
};

/**
 * Hook to get dynamic paint region status labels from Settings collection.
 * Falls back to default labels if no custom labels are configured.
 *
 * @returns Object with:
 * - `getPaintBlockLabels`: Function to get the display label for a given status
 * - `labels`: Record mapping each PaintRegionStatus to its display label
 * - `statusOptions`: Array of { value, label } for use in dropdowns
 */
export const usePaintRegionStatusLabels = () => {
  const paintRegionStatusLabels = useAtomValue(state.paintRegionStatusLabels);

  const labels = useMemo(() => {
    const result: Record<string, string> = {};

    // First, add all enum-based default labels
    Object.entries(DEFAULT_STATUS_LABELS).forEach(([key, value]) => {
      result[key] = value;
    });

    // Then, override with custom labels from Settings (and add any new custom statuses)
    if (paintRegionStatusLabels?.meta) {
      for (const metaItem of paintRegionStatusLabels.meta) {
        if (metaItem.__typename === 'StringMeta') {
          result[metaItem.key] = metaItem.value;
        }
      }
    }

    return result;
  }, [paintRegionStatusLabels]);

  const getPaintBlockLabels = useMemo(() => {
    return (status: string): string => {
      return labels[status] ?? status;
    };
  }, [labels]);

  const statusOptions = useMemo(() => {
    // Return all status options from labels (both enum-based and custom from DB)
    return Object.entries(labels).map(([key, value]) => ({
      value: key,
      label: value,
    }));
  }, [labels]);

  return {
    getPaintBlockLabels,
    labels,
    statusOptions,
  };
};

export { DEFAULT_STATUS_LABELS };
